import dayjs, { type ConfigType, type Dayjs } from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

/*
 * dayjs keeps one global plugin registry, so extending it belongs in one place.
 * PLAYDATA used to register `utc` and `timezone` again from its own module,
 * which meant the process had two modules racing to configure the same object
 * and no single answer to "which plugins are loaded".
 */
dayjs.extend(utc)
dayjs.extend(timezone)

export type DateInput = ConfigType

export function utcNow() {
  return dayjs.utc()
}

export function utcUnixMs() {
  return utcNow().valueOf()
}

export function utcDayjs(value?: DateInput) {
  const parsed = value === undefined || value === null ? utcNow() : dayjs.utc(value)
  return parsed.isValid() ? parsed : null
}

export function utcDate(value?: DateInput) {
  return utcDayjs(value)?.toDate() ?? null
}

export function utcIsoString(value?: DateInput) {
  return utcDayjs(value)?.toISOString() ?? null
}

export function utcDateOnly(value?: DateInput) {
  return utcDayjs(value)?.format("YYYY-MM-DD") ?? null
}

export function utcPathParts(value?: DateInput | Dayjs) {
  const parsed = utcDayjs(value) ?? utcNow()
  return {
    year: parsed.format("YYYY"),
    month: parsed.format("MM"),
    day: parsed.format("DD"),
  }
}

export function utcObjectKeyTimestamp(value?: DateInput | Dayjs) {
  return (utcIsoString(value) ?? utcNow().toISOString()).replace(/[:.]/g, "-")
}

export function unixMsDate(value: number) {
  return dayjs.utc(value).toDate()
}

/**
 * Formats an instant in a named zone.
 *
 * Publish dates are stored in UTC, but a date shown to a reader is a date in
 * some place. A post stored as 2025-01-19T19:24Z is "January 20, 2025" in
 * Tokyo, and formatting it in UTC would show the day before.
 */
export function zonedDate(value: DateInput, zone: string, format: string): string {
  if (!value) return ""
  const parsed = dayjs.utc(value)
  return parsed.isValid() ? parsed.tz(zone).format(format) : ""
}
